<template>
  <div id="dispute-details-dialog-box">
	<modal-dialog
		:title="'Dispute Details ' + (item.externalDisputeId ? 'for ' + item.externalDisputeId : '')"
	>
		<div slot="activator" class="activator-class">
			<v-btn outlined @click="viewDetails(item)" class="details-button">Details</v-btn>
		</div>
		<div slot="content">
			<div v-if="overallDetailsLoaded" class="overall-results">
				<h3>Overall Results</h3>
				<div class="overall-results-grid">
					<div class="grid-heading">Friendly Fraud</div>
					<div class="friendly-fraud-grid-details">
						<div class="old-fraud">
							<div class="decision-score">{{challengerDisputeDetailsOverallResultsOld['FriendlyFraud'].decisionScore}}</div>
							<v-chip :class="challengerDisputeDetailsOverallResultsOld['FriendlyFraud'].decisionLevel">{{challengerDisputeDetailsOverallResultsOld['FriendlyFraud'].decisionLevel[0].toUpperCase()}}</v-chip>
						</div>
						<v-icon>mdi-ray-start-arrow</v-icon>
						<div class="new-fraud">
							<div class="decision-score">{{challengerDisputeDetailsOverallResultsNew['FriendlyFraud'].decisionScore}}</div>
							<v-chip :class="challengerDisputeDetailsOverallResultsNew['FriendlyFraud'].decisionLevel">{{challengerDisputeDetailsOverallResultsNew['FriendlyFraud'].decisionLevel[0].toUpperCase()}}</v-chip>
						</div>
					</div>
					<div class="grid-heading">True Fraud</div>
					<div class="true-fraud-grid-details">
						<div class="old-fraud">
							<div class="decision-score">{{challengerDisputeDetailsOverallResultsOld['TrueFraud'].decisionScore}}</div>
							<v-chip :class="challengerDisputeDetailsOverallResultsOld['TrueFraud'].decisionLevel">{{challengerDisputeDetailsOverallResultsOld['TrueFraud'].decisionLevel[0].toUpperCase()}}</v-chip>
						</div>
						<v-icon>mdi-ray-start-arrow</v-icon>
						<div class="new-fraud">
							<div class="decision-score">{{challengerDisputeDetailsOverallResultsNew['TrueFraud'].decisionScore}}</div>
							<v-chip :class="challengerDisputeDetailsOverallResultsNew['TrueFraud'].decisionLevel">{{challengerDisputeDetailsOverallResultsNew['TrueFraud'].decisionLevel[0].toUpperCase()}}</v-chip>
						</div>
					</div>
				</div>
				<div class="final-decision">
					<v-chip :class="challengerDisputeDetailsOld.ARIADecision">{{challengerDisputeDetailsOld.ARIADecision}}</v-chip>
					<v-icon>mdi-ray-start-arrow</v-icon>
					<v-chip :class="challengerDisputeDetailsNew.ARIADecision">{{challengerDisputeDetailsNew.ARIADecision}}</v-chip>
				</div>
			</div>
			<div v-else>
				<v-skeleton-loader type=article></v-skeleton-loader>
			</div>
			<div class="factor-changes" v-if="factorChangesLoaded">
				<h3>Factor Changes</h3>
				<v-data-table
				:headers="factorChangesHeaders"
				:items="challengerDisputeDetailsFactorChanges"
				:items-per-page="10"
				>
					<template v-slot:item.score="{ item }">
						<div class="decision-scores">
							<div>{{ challengerDisputeDetailsFactorDecisionMapOld[item.factorId] ? challengerDisputeDetailsFactorDecisionMapOld[item.factorId].decisionScore : 'N/A' }}</div>
							<v-icon>mdi-ray-start-arrow</v-icon>
							<div>{{ challengerDisputeDetailsFactorDecisionMapNew[item.factorId] ? challengerDisputeDetailsFactorDecisionMapNew[item.factorId].decisionScore : 'N/A' }}</div>
						</div>
					</template>

				</v-data-table>
			</div>
			<div v-else>
				<v-skeleton-loader type="table"></v-skeleton-loader>
			</div>

		</div>
		<div slot="actions">
			<!-- No actions needed -->
		</div>
	</modal-dialog>
  </div>
</template>

<script>
	/**
	 * @fileoverview dispute-details-dialog-box.vue displays the details for disputes with different outcomes
	 * based on Champion Challenger. It relies on vuex to map the state variables from the store files.
	 * 
	 * @author Sophia Frankel <sophia.frankel@quavo.com>
	 */
	import { mapState } from 'vuex';

	export default {
		props: {
			/**
			 * Dispute object
			 */
			item: {
				Type: Object
			}
		},
		data() {
			return {
				overallDetailsLoaded: false,	// Loading flag for overall details
				factorChangesLoaded: false,		// Loading flag for factor changes
				/**
				 * Headers for factor changes data table
				 */
				factorChangesHeaders: [
					{
						text: "Evaluation Factor",
						align: "start",
						value: "label"
					},
					{
						text: "Fraud Type",
						align: "start",
						value: "type"
					},
					{
						text: "Score",
						align: "start",
						value: "score"
					},
				]
			}
		},
		computed: {
			...mapState({
				activeTenantId: (state) => state.account.activeTenantId,
				challengerDisputeDetailsOld: (state) => state.config.challengerDisputeDetailsOld,
				challengerDisputeDetailsNew: (state) => state.config.challengerDisputeDetailsNew,
				challengerDisputeDetailsOverallResultsOld: (state) => state.config.challengerDisputeDetailsOverallResultsOld,
				challengerDisputeDetailsOverallResultsNew: (state) => state.config.challengerDisputeDetailsOverallResultsNew,
				challengerDisputeDetailsFactorDecisionMapOld: (state) => state.config.challengerDisputeDetailsFactorDecisionMapOld,
				challengerDisputeDetailsFactorDecisionMapNew: (state) => state.config.challengerDisputeDetailsFactorDecisionMapNew,
				challengerDisputeDetailsFactorChanges: (state) => state.config.challengerDisputeDetailsFactorChanges,
				challengerTenantId: (state) => state.config.challengerTenantId
			})
		},
		watch: {
			/**
			 * Set overallDetailsLoaded to true when challengerDisputeDetailsOverallResultsNew update
		 	*/
			challengerDisputeDetailsOverallResultsNew() {
				this.overallDetailsLoaded = true;
			},
			/**
			 * Set factorChangesLoaded to true when challengerDisputeDetailsFactorChanges update
		 	*/
			challengerDisputeDetailsFactorChanges() {
				this.factorChangesLoaded = true;
			}
		},
		methods: {
			/**
			 * Set loading flags to false and retrieve the challenger result details
			 * @param {Object} item - dispute object
			 */
			viewDetails(item) {
				this.overallDetailsLoaded = false;
				this.factorChangesLoaded = false;

				let payload = {
					tenantId: this.activeTenantId,
					RequestId: item.RequestId,
					RequestSubId: item.RequestSubId
				};

				if (this.activeTenantId === 'master') {
					payload.tenantId = this.challengerTenantId;
				}

				this.$store.dispatch("retrieveChallengerResultDetails", payload);
			}
		}
	}
</script>

<style>
</style>